import {
  IonButton,
  IonButtons,
  IonCard,
  IonCardContent,
  IonCardHeader,
  IonCardSubtitle,
  IonCardTitle,
  IonChip,
  IonContent,
  IonHeader,
  IonIcon,
  IonItem,
  IonLabel,
  IonList,
  IonMenuButton,
  IonPage,
  IonSearchbar,
  IonSpinner,
  IonText,
  IonTitle,
  IonToolbar,
  useIonToast,
} from "@ionic/react";
import {
  calendarOutline,
  chevronBackOutline,
  chevronForwardOutline,
  timeOutline,
} from "ionicons/icons";
import moment from "moment";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router";
import PuntoServicioAPI from "src/services/puntoDeServicio";
import { IfetchPuntoServicio } from "src/services/PedidoDeProducto/pedido.producto.services";
import LimpiezasService, {
  LimpiezaFrecuenciaKey,
  LimpiezaPlanificacion,
  LimpiezaPlanificacionesPayload,
} from "src/services/limpiezasProfundas.service";
import "./LimpiezasProfundas.css";

const MIN_CHAR_SEARCH = 3;

const FRECUENCIAS: Array<{ key: LimpiezaFrecuenciaKey; label: string }> = [
  { key: "quincenal", label: "Quincenal" },
  { key: "mensual", label: "Mensual" },
  { key: "bimestral", label: "Bimestral" },
  { key: "trimestral", label: "Trimestral" },
  { key: "cuatrimestral", label: "Cuatrimental" },
  { key: "semestral", label: "Semestral" },
  { key: "anual", label: "Anual" },
];

const formatHorasEspeciales = (valor?: number | null) => {
  if (valor === null || valor === undefined || Number.isNaN(valor)) {
    return "-";
  }
  const fixed = Number.isInteger(valor) ? valor.toFixed(0) : valor.toFixed(1);
  return `${fixed} h`;
};

const crearFrecuenciasVacias = () =>
  FRECUENCIAS.reduce<Record<LimpiezaFrecuenciaKey, LimpiezaPlanificacion[]>>(
    (acc, freq) => {
      acc[freq.key] = [];
      return acc;
    },
    {
      quincenal: [],
      mensual: [],
      bimestral: [],
      trimestral: [],
      cuatrimestral: [],
      semestral: [],
      anual: [],
      otros: [],
    }
  );

const LimpiezasProfundas: React.FC = () => {
  const [buscarPS, setBuscarPS] = useState("");
  const [resultadosPS, setResultadosPS] = useState<IfetchPuntoServicio[]>([]);
  const [buscandoPS, setBuscandoPS] = useState(false);
  const [sinResultadosPS, setSinResultadosPS] = useState(false);
  const [seleccionado, setSeleccionado] = useState<IfetchPuntoServicio | null>(
    null
  );
  const [mesVisible, setMesVisible] = useState(moment().startOf("month"));
  const [planificaciones, setPlanificaciones] =
    useState<LimpiezaPlanificacionesPayload | null>(null);
  const [cargandoPlanificaciones, setCargandoPlanificaciones] = useState(false);
  const [present] = useIonToast();
  const history = useHistory();

  useEffect(() => {
    const psId = sessionStorage.getItem("lp_ps_id");
    const psCodigo = sessionStorage.getItem("lp_ps_codigo");
    const psNombre = sessionStorage.getItem("lp_ps_nombre");

    if (psId && psCodigo && psNombre) {
      const idNumero = Number(psId);
      if (!Number.isNaN(idNumero)) {
        setSeleccionado({
          id: idNumero,
          ps_codigo: psCodigo,
          ps_nombre: psNombre,
        } as IfetchPuntoServicio);
        setBuscarPS(psNombre);
        setResultadosPS([]);
        setSinResultadosPS(false);
      }
    }

    sessionStorage.removeItem("lp_ps_id");
    sessionStorage.removeItem("lp_ps_codigo");
    sessionStorage.removeItem("lp_ps_nombre");
  }, []);

  // Buscar puntos de servicio (solo cuando el usuario escribe al menos 3 caracteres)
  useEffect(() => {
    if (buscarPS.trim().length < MIN_CHAR_SEARCH) {
      setResultadosPS([]);
      setSinResultadosPS(false);
      return;
    }

    setBuscandoPS(true);
    const handle = setTimeout(() => {
      PuntoServicioAPI.listar(buscarPS.trim())
        .then((resp) => {
          const puntos = (resp.data?.datos || []) as IfetchPuntoServicio[];
          setResultadosPS(puntos);
          setSinResultadosPS(puntos.length === 0);
        })
        .catch(() => {
          setResultadosPS([]);
          setSinResultadosPS(true);
          present({
            message:
              "No se pudo obtener la lista de puntos de servicio. Intente nuevamente.",
            duration: 2500,
            color: "danger",
          });
        })
        .finally(() => setBuscandoPS(false));
    }, 450);

    return () => clearTimeout(handle);
  }, [buscarPS, present]);

  const limpiarSeleccion = useCallback(() => {
    setSeleccionado(null);
    setPlanificaciones(null);
    setBuscarPS("");
  }, []);

  const handleSeleccionarPS = (ps: IfetchPuntoServicio) => {
    setSeleccionado(ps);
    setBuscarPS(ps.ps_nombre);
    setResultadosPS([]);
    setSinResultadosPS(false);
  };

  const cambiarMes = (offset: number) => {
    setMesVisible((prev) => moment(prev).add(offset, "month"));
  };

  // Cargar planificaciones cuando se selecciona PS o cambia el mes visible
  useEffect(() => {
    if (!seleccionado || !seleccionado.ps_codigo) return;

    setCargandoPlanificaciones(true);
    LimpiezasService.obtenerPlanificaciones({
      psId: Number(seleccionado.id),
      psCodigo: seleccionado.ps_codigo,
      psNombre: seleccionado.ps_nombre,
      mes: mesVisible.month() + 1,
      anio: mesVisible.year(),
    })
      .then((datos) => setPlanificaciones(datos ?? null))
      .catch(() => {
        setPlanificaciones(null);
        present({
          message:
            "No fue posible obtener las planificaciones de Limpiezas Profundas.",
          duration: 2600,
          color: "danger",
        });
      })
      .finally(() => setCargandoPlanificaciones(false));
  }, [mesVisible, seleccionado, present]);

  const frecuenciasAgrupadas = useMemo(() => {
    if (!planificaciones?.frecuencias) {
      return crearFrecuenciasVacias();
    }
    return {
      ...crearFrecuenciasVacias(),
      ...planificaciones.frecuencias,
    };
  }, [planificaciones]);

  const hayPlanificaciones =
    !!planificaciones &&
    Object.values(planificaciones.frecuencias || {}).some(
      (lista) => lista && lista.length > 0
    );

  return (
    <IonPage className="lp-page">
      <IonHeader>
        <IonToolbar color="primary">
          <IonButtons slot="start">
            <IonMenuButton />
          </IonButtons>
          <IonTitle>Limpiezas Profundas</IonTitle>
        </IonToolbar>
      </IonHeader>

      <IonContent fullscreen>
        <section className="lp-search">
          <header className="lp-section-header">
            <IonIcon icon={calendarOutline} />
            <h2>Buscar Punto de Servicio</h2>
          </header>
          <IonSearchbar
            placeholder="Ingresa al menos 3 letras para buscar"
            value={buscarPS}
            debounce={0}
            onIonChange={(ev) => setBuscarPS(ev.detail.value ?? "")}
          />
          {buscandoPS && (
            <div className="lp-spinner">
              <IonSpinner />
            </div>
          )}
          {!buscandoPS && sinResultadosPS && (
            <IonText color="medium" className="lp-empty">
              No encontramos puntos asignados con esa busqueda.
            </IonText>
          )}
          <IonList className="lp-search-results">
            {resultadosPS.map((ps) => (
              <IonItem
                key={`${ps.id}-${ps.ps_codigo}`}
                button
                onClick={() => handleSeleccionarPS(ps)}
              >
                <IonLabel>
                  <h2>{ps.ps_nombre}</h2>
                  <p>{ps.ps_codigo}</p>
                </IonLabel>
              </IonItem>
            ))}
          </IonList>
        </section>

        {seleccionado && planificaciones && (
          <IonCard className="lp-card lp-card--ps">
            <IonCardHeader>
              <IonCardSubtitle>Punto de Servicio seleccionado</IonCardSubtitle>
              <IonCardTitle>{planificaciones.ps.nombre}</IonCardTitle>
            </IonCardHeader>
            <IonCardContent>
              <div className="lp-ps-grid">
                <div>
                  <span className="lp-ps-label">Codigo:</span>
                  <strong>{planificaciones.ps.codigo}</strong>
                </div>
                {planificaciones.ps.direccion && (
                  <div>
                    <span className="lp-ps-label">Direccion:</span>
                    <strong>{planificaciones.ps.direccion}</strong>
                  </div>
                )}
                {planificaciones.ps.unidad_negocio && (
                  <div>
                    <span className="lp-ps-label">Unidad de negocio:</span>
                    <strong>{planificaciones.ps.unidad_negocio}</strong>
                  </div>
                )}
                {typeof planificaciones.ps.horas_especiales_mes === "number" && (
                  <div>
                    <span className="lp-ps-label">
                      Horas especiales (mes):
                    </span>
                    <strong>
                      {formatHorasEspeciales(
                        planificaciones.ps.horas_especiales_mes
                      )}
                    </strong>
                  </div>
                )}
                {planificaciones.ps.contacto?.nombre && (
                  <div>
                    <span className="lp-ps-label">Contacto:</span>
                    <strong>
                      {planificaciones.ps.contacto?.nombre}{" "}
                      {planificaciones.ps.contacto?.telefono &&
                        `- ${planificaciones.ps.contacto?.telefono}`}
                    </strong>
                  </div>
                )}
              </div>
              <div className="lp-ps-actions">
                <IonButton
                  fill="outline"
                  size="small"
                  onClick={() => limpiarSeleccion()}
                >
                  Cambiar Punto
                </IonButton>
              </div>
            </IonCardContent>
          </IonCard>
        )}

        {seleccionado && (
          <section className="lp-calendar">
            <header className="lp-calendar-header">
              <IonButton
                fill="clear"
                onClick={() => cambiarMes(-1)}
                disabled={cargandoPlanificaciones}
              >
                <IonIcon icon={chevronBackOutline} slot="icon-only" />
              </IonButton>
              <div className="lp-calendar-header__title">
                {mesVisible.format("MMMM YYYY")}
              </div>
              <IonButton
                fill="clear"
                onClick={() => cambiarMes(1)}
                disabled={cargandoPlanificaciones}
              >
                <IonIcon icon={chevronForwardOutline} slot="icon-only" />
              </IonButton>
            </header>

            {planificaciones && (
              <div className="lp-summary">
                <IonChip color="primary">
                  Total: {planificaciones?.resumen?.total ?? 0}
                </IonChip>
                {FRECUENCIAS.map((freq) => {
                  const count =
                    planificaciones?.resumen?.por_frecuencia?.[freq.key] ?? 0;
                  if (!count) return null;
                  return (
                    <IonChip color="primary" key={freq.key}>
                      {freq.label}: {count}
                    </IonChip>
                  );
                })}
              </div>
            )}

            {cargandoPlanificaciones && (
              <div className="lp-spinner">
                <IonSpinner />
              </div>
            )}

            {!cargandoPlanificaciones && !hayPlanificaciones && (
              <IonText color="medium" className="lp-empty">
                No encontramos planificaciones para el mes seleccionado.
              </IonText>
            )}

            {!cargandoPlanificaciones && (
              <div className="lp-frequency-scroll">
                <div className="lp-frequency-row">
                {FRECUENCIAS.map((freq) => {
                  const items = frecuenciasAgrupadas[freq.key] || [];
                  return (
                    <div className="lp-calendar-column" key={freq.key}>
                      <div className="lp-column-title">{freq.label}</div>
                      {items.length === 0 && (
                        <p className="lp-column-empty">Sin planificaciones</p>
                      )}
                      {items.map((plan) => (
                        <article
                          key={`${plan.id}-${plan.fecha}`}
                          className="lp-plan-card"
                          role="button"
                          tabIndex={0}
                          onClick={() => history.push(`/limpiezas-profundas/${plan.id}`)}
                          onKeyDown={(ev) => {
                            if (ev.key === "Enter") {
                              history.push(`/limpiezas-profundas/${plan.id}`);
                            }
                          }}
                        >
                          <header>
                            <span className="lp-plan-date">
                              {moment(plan.fecha_iso || plan.fecha).format(
                                "DD MMM"
                              )}
                            </span>
                            <span
                              className={`lp-plan-state lp-plan-state--${plan.codigo_estado?.toLowerCase()}`}
                            >
                              {plan.estado}
                            </span>
                          </header>
                          <div className="lp-plan-type">
                            {plan.tipo_lavado || "Sin detalle"}
                          </div>
                          <div className="lp-plan-hours">
                            <IonIcon icon={timeOutline} />
                            <span>{plan.cant_horas} h</span>
                          </div>
                          <footer className="lp-plan-footer">
                            <span>{plan.origen}</span>
                          </footer>
                        </article>
                      ))}
                    </div>
                  );
                })}
                </div>
              </div>
            )}
          </section>
        )}

        {!seleccionado && (
          <IonText color="medium" className="lp-empty lp-empty--hint">
            Selecciona un Punto de Servicio para ver su calendario de
            planificaciones.
          </IonText>
        )}
      </IonContent>
    </IonPage>
  );
};

export default LimpiezasProfundas;
